import { useState, useCallback, useEffect } from 'react';
import { toLocalYMD } from '../../../utils/dateUtils';
import api from '../../../utils/api';
import { loadClubMapLayers } from '../utils/clubMapLayers';

export function useClubFeatures(clubId) {
  const [huntBoundaries, setHuntBoundaries] = useState([]);
  const [huntLocations, setHuntLocations] = useState([]);
  const [clubLines, setClubLines] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const listClubFeatures = useCallback(async () => {
    if (!clubId) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const { club, clubMapId, boundaries, locations, lines } = await loadClubMapLayers(api, clubId);
      console.log('Club hunt boundaries loaded:', boundaries);
      if (boundaries.length > 0) {
        console.log('Boundary 0 style fields:', {
          line_color: boundaries[0].line_color,
          fill_color: boundaries[0].fill_color,
          fill_opacity: boundaries[0].fill_opacity
        });
      }
      console.log('Club hunt locations loaded:', locations);
      console.log('Club shared line features loaded:', lines);

      setHuntBoundaries(boundaries);
      setHuntLocations(locations);
      setClubLines(lines);

      return {
        club,
        clubMapId,
        boundaries,
        locations,
        lines
      };
    } catch (e) {
      console.error('Failed to load club features:', e);
      setError(e.message || 'Failed to load club features');
      if (e.response?.status !== 401) {
        alert('Failed to load club features');
      }
      throw e;
    } finally {
      setLoading(false);
    }
  }, [clubId]);

  // Clear previous club features immediately when clubId changes to avoid leakage/flicker
  useEffect(() => {
    setHuntBoundaries([]);
    setHuntLocations([]);
    setClubLines([]);
    // Do not auto-fetch here; caller effect in UnifiedMapPage handles fetching
  }, [clubId]);

  // Convert club features to a format compatible with the map
  const getMapFeatures = useCallback(() => {
    const features = [];
    const today = toLocalYMD(new Date());
    
    // Convert hunt boundaries to features
    huntBoundaries.forEach(boundary => {
      features.push({
        id: `boundary-${boundary.id}`,
        name: boundary.name,
        type: 'polygon',
        geojson: boundary.geojson,
        notes: boundary.notes,
        // Forward optional style fields to map properties
        line_color: boundary.line_color || undefined,
        fill_color: boundary.fill_color || undefined,
        fill_opacity: typeof boundary.fill_opacity === 'number' ? boundary.fill_opacity : undefined,
        featureType: 'hunt_boundary',
        acreage: boundary.acreage,
        is_boundary: boundary.is_boundary !== false,
      });
    });
    
    // Convert hunt locations to features
      huntLocations.forEach(location => {
      // Check if location is occupied for today
      const isOccupied = location.sign_out_records?.some(record => record.date === today) || false;
      
      features.push({
        id: `location-${location.id}`,
        name: location.name,
        type: 'point',
        geojson: location.geojson,
        notes: location.notes,
        icon: location.icon || 'default',
        featureType: 'hunt_location',
          isClub: true,
        // Ensure fields used across components are present with backend names
        feature_type: location.feature_type,
        optimal_wind: location.optimal_wind,
        optimal_winds: location.optimal_winds || [],
        feeding_mode: location.feeding_mode,
        feed_capacity_lbs: location.feed_capacity_lbs,
        consumption_rate_code: location.consumption_rate_code,
        refill_threshold_pct: location.refill_threshold_pct,
        feeding_is_active: location.feeding_is_active,
        // Keep legacy/camel keys for any older code paths
        locationType: location.feature_type,
        optimalWind: location.optimal_wind,
        optimalWinds: location.optimal_winds || [],
          radius: location.radius,
          // Feeding computed fields (if configured on backend)
          percent_remaining: location.percent_remaining,
          estimated_remaining_lbs: location.estimated_remaining_lbs,
          refill_due_date: location.refill_due_date,
        signOutRecords: location.sign_out_records,
        isOccupied: isOccupied // Add this field
      });
    });

    // Convert club-shared personal line features
    clubLines.forEach(line => {
      features.push({
        id: `clubline-${line.id}`,
        name: line.name,
        type: 'line',
        geojson: line.geojson,
        notes: line.notes,
        length: line.length || null,
        featureType: 'club_line'
      });
    });
    
    return features;
  }, [huntBoundaries, huntLocations, clubLines]);

  return {
    huntBoundaries,
    huntLocations,
    clubLines,
    loading,
    error,
    listClubFeatures,
    getMapFeatures
  };
}
